import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const FIELD_LABELS = {
  warn_low_f: "Warn low",
  warn_high_f: "Warn high",
  critical_low_f: "Critical low",
  critical_high_f: "Critical high",
};

const FIELD_HELP = {
  warn_low_f: "Below this triggers a WARN alert.",
  warn_high_f: "Above this triggers a WARN alert.",
  critical_low_f: "Below this triggers a CRITICAL alert.",
  critical_high_f: "Above this triggers a CRITICAL alert.",
};

function ThresholdForm({ initial, onSaved }) {
  const [draft, setDraft] = useState({ ...initial });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const setField = (k, v) => setDraft({ ...draft, [k]: v });

  const save = async () => {
    setBusy(true);
    setError("");
    const body = {};
    [
      "warn_low_f",
      "warn_high_f",
      "critical_low_f",
      "critical_high_f",
    ].forEach((k) => {
      const v = draft[k];
      body[k] = v === "" || v == null ? null : Number(v);
    });
    body.sustained_seconds = Number(draft.sustained_seconds);
    body.renotify_seconds = Number(draft.renotify_seconds);
    try {
      const { data } = await api.patch(`/thresholds/${initial.sensor_id}`, body);
      toast.success(`Thresholds saved for ${initial.sensor_id}`);
      onSaved?.(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`threshold-form-${initial.sensor_id}`}
      className="border border-zinc-800 bg-zinc-900/40 p-6 space-y-4"
    >
      <div className="font-display text-xl font-bold uppercase tracking-tight">
        {initial.sensor_id.replace("food_", "Walk-in ")}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {Object.keys(FIELD_LABELS).map((k) => (
          <div key={k}>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              {FIELD_LABELS[k]} (°F)
            </Label>
            <Input
              type="number"
              step="0.1"
              data-testid={`threshold-${initial.sensor_id}-${k}`}
              value={draft[k] ?? ""}
              onChange={(e) => setField(k, e.target.value)}
              placeholder="blank = disabled"
              className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm"
            />
            <div className="text-[10px] text-zinc-500 mt-1">{FIELD_HELP[k]}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4 pt-2 border-t border-zinc-800">
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-400">
            Sustained (minutes before alert fires)
          </Label>
          <Input
            type="number"
            min="1"
            data-testid={`threshold-${initial.sensor_id}-sustained_minutes`}
            value={Math.round((draft.sustained_seconds || 0) / 60)}
            onChange={(e) =>
              setField("sustained_seconds", Math.max(60, Number(e.target.value || 0) * 60))
            }
            className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-400">
            Renotify every (minutes, min 5)
          </Label>
          <Input
            type="number"
            min="5"
            data-testid={`threshold-${initial.sensor_id}-renotify_minutes`}
            value={Math.round((draft.renotify_seconds || 0) / 60)}
            onChange={(e) =>
              setField("renotify_seconds", Math.max(300, Number(e.target.value || 0) * 60))
            }
            className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm"
          />
        </div>
      </div>

      {error && (
        <div
          data-testid={`threshold-error-${initial.sensor_id}`}
          className="text-sm text-red-400 border-l-2 border-red-500 pl-3"
        >
          {error}
        </div>
      )}

      <Button
        onClick={save}
        disabled={busy}
        data-testid={`threshold-save-${initial.sensor_id}`}
        className="rounded-sm bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold"
      >
        Save
      </Button>
    </div>
  );
}

export default function Thresholds() {
  const [list, setList] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/thresholds");
      setList(data);
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
          Admin
        </div>
        <h1 className="font-display text-4xl font-black uppercase tracking-tight">
          Thresholds
        </h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-xl">
          Leave a field blank to disable that side of the band. Sustained = how
          long out-of-range before an alert fires. Renotify = how often to
          re-send SMS while the alert is open and unacknowledged.
        </p>
      </div>

      {error && (
        <div data-testid="thresholds-error" className="mb-6 text-sm text-red-400 border-l-2 border-red-500 pl-3">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {list.map((t) => (
          <ThresholdForm key={t.sensor_id} initial={t} onSaved={load} />
        ))}
      </div>
    </AppShell>
  );
}
